import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Sparkles, Zap, Target, BarChart3, Users, Download } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_62e9b7c6-9db9-450d-9189-7454ca6b59d7/artifacts/nhj2mgws_Asset%203BRAIN-GL.png";
const MASCOT_URL = "https://customer-assets.emergentagent.com/job_brandbrain-ai/artifacts/wegfskta_SILLON.png";

export default function LandingPage() {
  const { login } = useAuth();
  const { t, language, changeLanguage } = useLanguage();

  const features = [
    {
      icon: Sparkles,
      title: language === 'es' ? 'Copys Creativos' : 'Creative Copies',
      description: language === 'es' 
        ? 'Genera copys impactantes con estructura Hook, Solución, Beneficio y Cierre' 
        : 'Generate impactful copies with Hook, Solution, Benefit and Closing structure'
    },
    {
      icon: Target,
      title: language === 'es' ? 'Multi-Medio' : 'Multi-Media',
      description: language === 'es'
        ? 'Radio, TV, Reels, Stories y Posts adaptados a cada plataforma'
        : 'Radio, TV, Reels, Stories and Posts adapted to each platform'
    },
    {
      icon: Zap,
      title: language === 'es' ? 'Prompts de Imagen' : 'Image Prompts',
      description: language === 'es'
        ? 'Prompts JSON listos para usar en tu generador de imágenes favorito'
        : 'JSON prompts ready to use in your favorite image generator'
    },
    {
      icon: BarChart3,
      title: language === 'es' ? 'Estadísticas' : 'Statistics',
      description: language === 'es'
        ? 'Rastrea tu productividad y las ideas generadas por marca'
        : 'Track your productivity and ideas generated per brand'
    },
    {
      icon: Users,
      title: language === 'es' ? 'Colaboración' : 'Collaboration',
      description: language === 'es'
        ? 'Trabaja en equipo compartiendo marcas e ideas'
        : 'Work as a team sharing brands and ideas'
    },
    {
      icon: Download,
      title: language === 'es' ? 'Exportación PDF' : 'PDF Export',
      description: language === 'es'
        ? 'Descarga tus ideas como briefs creativos profesionales'
        : 'Download your ideas as professional creative briefs'
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src={LOGO_URL} alt="Brain-GL" className="h-10 object-contain" />
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
                className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                data-testid="language-toggle"
              >
                {language === 'es' ? 'EN' : 'ES'}
              </button>
              <Button 
                onClick={login}
                className="bg-lime-500 text-black hover:bg-lime-600 font-medium"
                data-testid="login-btn"
              >
                {t('loginWithGoogle')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -right-20 w-96 h-96 bg-lime-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-red-600/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-lime-500/10 border border-lime-500/20 rounded-full">
                <Sparkles className="w-4 h-4 text-lime-500" />
                <span className="text-sm text-lime-500">Marketing Inteligente</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <span className="text-white">{language === 'es' ? 'Potencia tu' : 'Power Your'}</span>
                <br />
                <span className="text-lime-500">{language === 'es' ? 'Creatividad' : 'Creativity'}</span>
                <br />
                <span className="text-red-500">{language === 'es' ? 'con IA' : 'with AI'}</span>
              </h1>
              
              <p className="text-lg text-zinc-400 max-w-lg">
                {t('heroSubtitle')}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={login}
                  size="lg"
                  className="bg-lime-500 text-black hover:bg-lime-600 font-semibold px-8 py-6 text-lg shadow-[0_4px_14px_0_rgba(132,204,22,0.39)] hover:shadow-[0_6px_20px_rgba(132,204,22,0.23)] transition-all hover:-translate-y-0.5"
                  data-testid="get-started-btn"
                >
                  {t('getStarted')}
                </Button>
              </div>
            </div>
            
            <div className="relative flex justify-center lg:justify-end animate-fade-in">
              <img 
                src={MASCOT_URL} 
                alt="Brain-GL Mascot" 
                className="w-80 h-80 object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {language === 'es' ? 'Todo lo que necesitas' : 'Everything you need'}
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              {language === 'es' 
                ? 'Herramientas profesionales para creativos y equipos de marketing'
                : 'Professional tools for creatives and marketing teams'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="group bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-6 hover:border-lime-500/30 transition-all duration-300 relative overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-lime-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-lime-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-lime-500/20 transition-colors">
                      <Icon className="w-6 h-6 text-lime-500" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-zinc-400 text-sm">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-lime-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-red-600/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
                {language === 'es' ? '¿Listo para empezar?' : 'Ready to start?'}
              </h2>
              <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
                {language === 'es'
                  ? 'Únete a miles de creativos que ya potencian sus ideas con Brain-GL'
                  : 'Join thousands of creatives already powering their ideas with Brain-GL'}
              </p>
              <Button 
                onClick={login}
                size="lg"
                className="bg-lime-500 text-black hover:bg-lime-600 font-semibold px-8 py-6 text-lg"
                data-testid="cta-btn"
              >
                {t('getStarted')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Brain-GL" className="h-8 object-contain" />
          <p className="text-zinc-500 text-sm">
            © {new Date().getFullYear()} Brain-GL by Grupo Locoff. {language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
